import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useContext, useMemo, useState } from 'react';

import { ServiceFormTemplate, Step } from '@/widgets/ServiceFormTemplate';

import {
  Payment,
  PlaceOfResidence,
  placeOfResidenceSchema,
  SignUp,
  WeaponSelection,
  weaponSelectionSchemaWithPhoto,
} from '@/features/ui';
import { AdditionalDocuments } from '@/features/ui/Steps/AdditionalDocuments';
import { additionalDocumentsSchema } from '@/features/ui/Steps/AdditionalDocuments/model/schema';
import { InsuranceData } from '@/features/ui/Steps/InsuranceData';
import { insuranceDataSchema } from '@/features/ui/Steps/InsuranceData/model/schemas';
import { PersonalAndPassportData, personalAndPassportDataSchema } from '@/features/ui/Steps/PersonalAndPassportData';

import { InvoiceStatus } from '@/shared/types';

import { useFormDraftPlainTools } from '@/hooks/useFormDraftPlainTools';
import { usePaymentShortPolling } from '@/hooks/usePaymentShortPolling';

import { ServiceFileManager } from '@/helpers/serviceFileManager';

import { ServicesContext } from '@/contexts/Services/ServicesContext';

import { FormDataType, UserRequest, UserRequestDraft, UserService, UserServicePaymentType } from '@/api/types';

import api from '@/api';

import {
  disallowedDocumentTypeCodesForAdditionalDocumentStep,
  SERVICE_CODE,
  STEP_NUMBER_SIGN_UP,
  STEP_NUMBER_TO_PAY,
} from './lib/constants';
import { generatePayload } from './lib/generatePayload';
import { expertResearchDataSchema } from './model/schemas';
import { ServiceProps } from './model/types';
import { DataPreview } from './ui/steps/DataPreview';
import { ExpertResearchStep } from './ui/steps/ExpertResearchStep';
import { StorageForCollectionResultStep } from './ui/steps/StorageForCollectionResultStep';

export const STEPS: Array<Step<ServiceProps>> = [
  {
    title: 'Вибір зброї',
    step: WeaponSelection,
    validationSchema: weaponSelectionSchemaWithPhoto,
  },
  {
    title: 'Експертні дослідження',
    step: ExpertResearchStep,
    validationSchema: expertResearchDataSchema,
  },
  {
    title: 'Особисті та паспортні дані',
    step: PersonalAndPassportData,
    validationSchema: personalAndPassportDataSchema,
  },
  {
    title: 'Місце зберігання зброї',
    step: PlaceOfResidence,
    validationSchema: placeOfResidenceSchema,
  },
  {
    title: 'Страхування',
    step: InsuranceData,
    validationSchema: insuranceDataSchema,
  },
  {
    title: 'Додаткові дані',
    step: AdditionalDocuments,
    validationSchema: additionalDocumentsSchema,
  },
  {
    title: 'Підтвердження даних',
    step: DataPreview,
  },
  {
    title: 'Підписання документу КЕП',
    step: SignUp,
  },
  {
    title: 'Оплата',
    step: Payment,
  },
  {
    title: 'Результат',
    step: StorageForCollectionResultStep,
  },
];

export const ColdSteelStorageForCollection: React.FC = () => {
  const { getServiceByCode } = useContext(ServicesContext);
  const currentService = getServiceByCode(SERVICE_CODE) as UserService;
  const serviceFileManager = useMemo(() => new ServiceFileManager(), []);
  const [defaultValues, setDefaultValues] = useState<FormDataType>({});
  const {
    isLoading: isLoadingDraft,
    onDraftCreate,
    onDraftContinue,
    onDraftSave,
    onPaymentContinue,
    currentRequestId,
  } = useFormDraftPlainTools({
    serviceCode: SERVICE_CODE,
    setData: (data) => {
      setDefaultValues(data ?? {});
    },
    stepNumberToPay: STEP_NUMBER_TO_PAY,
    stepNumberToSignUp: STEP_NUMBER_SIGN_UP,
  });

  const { mutateAsync: generatePDF, isPending: isLoadingPdf } = useMutation({
    mutationFn: async ({
      code,
      payload,
    }: {
      code: Parameters<typeof api.services.generatePdf>[0];
      payload: Parameters<typeof api.services.generatePdf>[1];
    }) => {
      const result = await api.services.generatePdf(code, payload);
      serviceFileManager.setAttachment(result);
      return result;
    },
  });

  const [redirectServiceInfo, setRedirectServiceInfo] = useState<{
    link: string;
    request_id: number;
  }>();

  const { mutateAsync: submitForm, isPending: isLoadingSubmit } = useMutation({
    mutationFn: ({
      code,
      payload,
      allowToProfileUpdate,
    }: {
      code: Parameters<typeof api.services.submit>[0];
      payload: Parameters<typeof api.services.submit>[1];
      allowToProfileUpdate?: string;
    }) => api.services.submit(code, payload, allowToProfileUpdate),
  });
  const [currentUserRequest, setCurrentUserRequest] = useState<Nullable<UserRequest>>(null);
  const { isShortPollingSuccessed, isShortPollingInProgress, isShortPollingFailed, startPaymentShortPolling } =
    usePaymentShortPolling(setCurrentUserRequest);

  const onGercPaymentContinue = async (userRequestId: UserRequest['id']): Promise<number> => {
    if (sessionStorage.getItem('redirectServiceInfo')) {
      setRedirectServiceInfo(JSON.parse(sessionStorage.getItem('redirectServiceInfo') as string));
      sessionStorage.removeItem('redirectServiceInfo');
    }

    startPaymentShortPolling(userRequestId);

    return onPaymentContinue(userRequestId);
  };

  const handleDraftCreate = async (_: any, specificStep?: (step: number) => void): Promise<Nullable<string>> =>
    specificStep &&
    onDraftCreate(
      {
        id: currentService.id,
        name: currentService.name,
        code: SERVICE_CODE,
        totalSteps: STEPS.length,
      },
      specificStep,
    );

  const handleGeneratePDF = useCallback(
    async (formData: FormDataType): Promise<void> => {
      const { commonPayload } = await generatePayload({
        requestId: (currentRequestId ?? '') as UserRequestDraft['id'],
        formData,
        service: currentService,
      });
      await generatePDF({ code: SERVICE_CODE, payload: commonPayload });
    },
    [currentRequestId, currentService, generatePDF],
  );

  const handleSubmitForm = useCallback(
    async (formData: FormDataType): Promise<void> => {
      const { signedPayload } = await generatePayload({
        requestId: (currentRequestId ?? '') as UserRequestDraft['id'],
        formData,
        service: currentService,
        serviceFileManager,
      });

      await submitForm({
        code: SERVICE_CODE,
        payload: signedPayload,
        allowToProfileUpdate: String(formData.allowProfileUpdate),
      });

      serviceFileManager.download();
      serviceFileManager.clear();
    },
    [currentRequestId, currentService, serviceFileManager, submitForm],
  );

  const isLoading = isLoadingDraft || isLoadingPdf || isLoadingSubmit;

  return (
    <ServiceFormTemplate
      className="storage-for-collection"
      serviceCode={SERVICE_CODE}
      title={currentService.name}
      steps={STEPS}
      isLoading={isLoading}
      defaultValues={defaultValues}
      currentRequestId={currentRequestId ?? null}
      onDraftCreate={handleDraftCreate}
      onDraftContinue={onDraftContinue}
      onDraftSave={onDraftSave}
      onGercPaymentContinue={onGercPaymentContinue}
      onPaymentContinue={onPaymentContinue}
      stepProps={{
        handleGeneratePDF,
        serviceFileManager,
        submitForm: handleSubmitForm,
        paymentShortPolling: { isShortPollingInProgress },
        finishedUserRequestStatus: isShortPollingSuccessed || isShortPollingFailed,
        userRequestId: currentRequestId ?? currentUserRequest?.id ?? '',
        allowedPaymentType: currentService.paymentType ?? UserServicePaymentType.WITHOUT_PAYMENT,
        withWeaponPhotoUpload: true,
        isInsuranceMandatory: false,
        showNpuPaymentInformer: true,
        invoiceStatus: currentUserRequest?.invoice?.status ?? InvoiceStatus.SUCCESS,
        beforeRedirectToGerc: () => {
          if (redirectServiceInfo) {
            sessionStorage.setItem('redirectServiceInfo', JSON.stringify(redirectServiceInfo));
          }
        },
        shouldBeAbleToUpdateProfile: currentService.sync_profile_available ?? false,
        disallowedDocumentTypeCodes: disallowedDocumentTypeCodesForAdditionalDocumentStep,
      }}
    />
  );
};
